import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { BreadcrumbService } from "src/app/services/breadcrumb.service";
import { ScanFilterStateService } from "src/app/services/scan-filter-state.service";
import { ProductMasterService } from "src/app/services/masters/product-master.service";
import { ProductMasterResponseForDdl } from "src/app/models/Masters/productMaster";
import {
  parseTrendGraph,
  QrLifeCycleInsights,
  QrLifeCycleReportItem,
  QrLifeCycleRequest,
  QrLifeCycleResponse,
  QrLifeCycleService,
} from "src/app/services/home/qr-lifecycle.service";
import { apiErrorMessage, reportErrorState } from "src/app/shared/api-error.util";
type QrReportType =
  | "generated"
  | "assigned"
  | "scrapped"
  | "deallocated"
  | "instock";

interface ProductFilterOption {
  id: number | null;
  name: string;
}
interface QrReportConfig {
  breadcrumbLabel: string;
  pageTitle: string;
  infoTooltip: string;
  tableTitle: string;
  activeKpi:
    | "generatedQr"
    | "assignedQr"
    | "scrappedQr"
    | "deallocatedQr"
    | "inStockQr";
  /** Value sent as @Status to usp_GetQRLifeCycleDashboard. */
  apiStatus:
    | "Generated"
    | "Assigned"
    | "Scrapped"
    | "Deallocated"
    | "Available";
  /** IssueNo (Generated) has no product of its own - that's assigned later,
   *  at allocation time - so its rows never show a Product column. Assigned/
   *  Deallocated/Scrapped all resolve to a real product via the SP's join
   *  to the product master table, so they do. Available (in-stock) ranges
   *  also have no product yet - they're QR numbers waiting to be allocated. */
  showProduct: boolean;
  /** Only Assigned/Deallocated carry Batch No, Mfg Date and Exp Date - those
   *  are set at allocation time. Generated and Available have none yet;
   *  Scrapped intentionally drops them (see usp_GetQRLifeCycleDashboard's
   *  SCRAPPED REPORT branch). */
  showBatchMfgExp: boolean;
}
const REPORT_CONFIGS: Record<QrReportType, QrReportConfig> = {
  generated: {
    breadcrumbLabel: "Generated QR Report",
    pageTitle: "Generated QR Report",
    infoTooltip: "QR codes generated across all batches.",
    tableTitle: "Generated QR Details",
    activeKpi: "generatedQr",
    apiStatus: "Generated",
    showProduct: false,
    showBatchMfgExp: false,
  },
  assigned: {
    breadcrumbLabel: "Assigned QR Report",
    pageTitle: "Assigned QR Report",
    infoTooltip: "QR codes assigned to a batch and ready for print/dispatch.",
    tableTitle: "Assigned QR Details",
    activeKpi: "assignedQr",
    apiStatus: "Assigned",
    showProduct: true,
    showBatchMfgExp: true,
  },
  scrapped: {
    breadcrumbLabel: "Scrapped QR Report",
    pageTitle: "Scrapped QR Report",
    infoTooltip: "QR codes discarded due to print/production defects.",
    tableTitle: "Scrapped QR Details",
    activeKpi: "scrappedQr",
    apiStatus: "Scrapped",
    showProduct: true,
    showBatchMfgExp: false,
  },
  deallocated: {
    breadcrumbLabel: "Deallocated QR Report",
    pageTitle: "Deallocated QR Report",
    infoTooltip:
      "QR codes unassigned from a batch and returned to the available pool.",
    tableTitle: "Deallocated QR Details",
    activeKpi: "deallocatedQr",
    apiStatus: "Deallocated",
    showProduct: true,
    showBatchMfgExp: true,
  },
  instock: {
    breadcrumbLabel: "In-Stock QR Report",
    pageTitle: "In-Stock QR Report",
    infoTooltip:
      "QR codes generated but not yet assigned to a batch - available in the pool.",
    tableTitle: "In-Stock QR Details",
    activeKpi: "inStockQr",
    // "Available" is a derived number (Generated - Assigned - Deallocated -
    // Scrapped) for the KPI card, but usp_GetQRLifeCycleDashboard also
    // returns real in-stock QR ranges as row-level detail here, via the
    // same gap/island technique usp_GetIssueNoMapping uses.
    apiStatus: "Available",
    showProduct: false,
    showBatchMfgExp: false,
  },
};
const emptyInsights = (): QrLifeCycleInsights => ({
  generatedQR: 0,
  assignedQR: 0,
  deallocatedQR: 0,
  scrappedQR: 0,
  availableQR: 0,
  comparePeriodLabel: "",
  fromDate: "",
  toDate: "",
  previousFromDate: "",
  previousToDate: "",
  generatedPercentage: 0,
  assignedPercentage: 0,
  deallocatedPercentage: 0,
  scrappedPercentage: 0,
  availablePercentage: 0,
  generatedComparedToPrevious: 0,
  assignedComparedToPrevious: 0,
  deallocatedComparedToPrevious: 0,
  scrappedComparedToPrevious: 0,
  availableComparedToPrevious: 0,
});
@Component({
  selector: "app-generated-qr-report",
  templateUrl: "./generated-qr-report.component.html",
  styleUrls: ["./generated-qr-report.component.css"],
})
export class GeneratedQrReportComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  breadcrumb: string[] = [];
  reportType: QrReportType = "generated";
  config: QrReportConfig = REPORT_CONFIGS.generated;
  isLightMode = document.body.classList.contains("light-mode");
  isLoading = false;
  loadError = "";
  /** Set alongside loadError, drives the full-page ReportErrorStateComponent
   *  (see showFullPageError) - null while there's no error. */
  loadErrorState: { title: string; message: string } | null = null;
  user = JSON.parse(localStorage.getItem("user") || "{}");
  compId = Number(this.user.compId) || 1;
  productId = 0;
  fromDate: string | null = null;
  toDate: string | null = null;
  insights: QrLifeCycleInsights = emptyInsights();
  // Parsed from the API's trendGraph (see parseTrendGraph()/applyResponse()) -
  // drives each KPI card's own sparkline below.
  generatedTrendData: number[] = [];
  assignedTrendData: number[] = [];
  scrappedTrendData: number[] = [];
  deallocatedTrendData: number[] = [];
  availableTrendData: number[] = [];
  searchTerm = "";
  private searchDebounce: any = null;
  reportData: QrLifeCycleReportItem[] = [];
  rows = 5;
  rowsPerPageOptions = [5, 10, 25, 50, 100];
  currentPage = 1;
  totalRecords = 0;
  totalPages = 1;
  filterProductOptions: ProductFilterOption[] = [];
  pendingProductFilter: ProductFilterOption | null = null;
  appliedProductFilter: ProductFilterOption | null = null;
  productSearchTerm = "";
  isFilterDrawerOpen = false;
  /** Which of the drawer's own dropdowns is open, or none. */
  openDropdown: "product" | null = null;

  private themeChangeHandler = (): void => {
    this.isLightMode = document.body.classList.contains("light-mode");
  };
  private exportBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#exportBtn")) {
      e.stopPropagation();
      this.exportTable();
    }
  };
  private syncBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#syncBtn")) {
      e.stopPropagation();
      this.refreshData();
    }
  };
  private filtersBtnClickHandler = (e: any): void => {
    if (
      e.target &&
      e.target.closest &&
      e.target.closest("#filtersTriggerBtn")
    ) {
      e.stopPropagation();
      this.openFilterDrawer();
    }
  };
  private dateRangeSub: Subscription | null = null;
  private routeParamSub: Subscription | null = null;
  /** True once both the initial route param and the initial date-range
   *  value have been applied - see ngOnInit's comment. */
  private initialized = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public breadcrumbService: BreadcrumbService,
    private qrLifeCycleService: QrLifeCycleService,
    private scanFilterState: ScanFilterStateService,
    private productMasterService: ProductMasterService,
  ) {}

  /** Every crumb but the last points back to the dashboard - same reasoning
   *  as HeaderComponent.goToDashboard(), now that the breadcrumb is rendered
   *  in the page topbar instead of the navbar. */
  goToDashboard(): void {
    this.router.navigate(["/dashboard1"]);
  }

  ngOnInit(): void {
    window.addEventListener("sa-theme-change", this.themeChangeHandler);
    this.loadFilterProducts();

    this.routeParamSub = this.route.paramMap.subscribe((params) => {
      const requestedType = (params.get("type") ||
        this.route.snapshot.data["type"] ||
        "generated") as QrReportType;
      this.reportType = REPORT_CONFIGS[requestedType]
        ? requestedType
        : "generated";
      this.config = REPORT_CONFIGS[this.reportType];
      this.breadcrumb = ["Dashboard", this.config.breadcrumbLabel];
      this.breadcrumbService.items = this.breadcrumb;
      // Product filter is drawer-local state per report - clear it rather
      // than carry, say, "Engine Oil" over from Assigned into Scrapped.
      this.pendingProductFilter = null;
      this.appliedProductFilter = null;
      this.productId = 0;
      // Except on first mount: if a product was already selected elsewhere
      // (the dashboard, another report) via ScanFilterStateService, and
      // this report type actually supports a product filter, start already
      // filtered by it - same cross-page pickup as ScanFilterStateService's
      // dateRange$ below.
      if (
        !this.initialized &&
        this.config.showProduct &&
        this.scanFilterState.productId
      ) {
        this.productId = this.scanFilterState.productId;
        this.appliedProductFilter = {
          id: this.scanFilterState.productId,
          name: this.scanFilterState.product || "",
        };
      }
      this.updateHeaderFiltersBadge();
      this.currentPage = 1;
      if (this.initialized) this.loadDashboard();
    });

    // dateRange$ is a BehaviorSubject, so it (like paramMap above) emits its
    // current value synchronously during this subscribe() call - both have
    // now set their initial state by the time execution reaches below, so
    // flipping `initialized` here and loading once avoids double-fetching
    // on first mount while still reacting to every later change from either.
    this.dateRangeSub = this.scanFilterState.dateRange$.subscribe((range) => {
      this.fromDate = range.dateFrom;
      this.toDate = range.dateTo;
      this.currentPage = 1;
      if (this.initialized) this.loadDashboard();
    });

    this.initialized = true;
    this.loadDashboard();
  }
  ngAfterViewInit(): void {
    document.addEventListener("click", this.exportBtnClickHandler);
    document.addEventListener("click", this.syncBtnClickHandler);
    document.addEventListener("click", this.filtersBtnClickHandler);
    this.updateHeaderFiltersBadge();
  }
  ngOnDestroy(): void {
    document.removeEventListener("click", this.exportBtnClickHandler);
    document.removeEventListener("click", this.syncBtnClickHandler);
    document.removeEventListener("click", this.filtersBtnClickHandler);
    window.removeEventListener("sa-theme-change", this.themeChangeHandler);
    if (this.dateRangeSub) this.dateRangeSub.unsubscribe();
    if (this.routeParamSub) this.routeParamSub.unsubscribe();
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.breadcrumbService.items = [];
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    this.openDropdown = null;
  }

  toggleDropdown(which: "product", ev: Event): void {
    ev.stopPropagation();
    this.openDropdown = this.openDropdown === which ? null : which;
  }

  /** Populates filterProductOptions from the product master's DDL endpoint -
   *  the full active product catalog for the company - the same fix applied
   *  to analytics.component.ts's own product filter (see loadFilterProducts
   *  there): a QR range's own rows aren't a reliable source of "every
   *  product that could be filtered on". */
  private loadFilterProducts(): void {
    if (!this.compId) return;
    this.productMasterService
      .loadProductsForDdl({
        pageSize: -1,
        isActive: "1",
        compId: this.compId,
        searchValue: "",
      })
      .subscribe({
        next: (response: ProductMasterResponseForDdl) => {
          if (response.responseCode === "200" && response.details) {
            this.filterProductOptions = response.details
              .map((p) => ({ id: p.productId, name: p.productNm }))
              .sort((a, b) => a.name.localeCompare(b.name));
          }
        },
        error: (error) => {
          console.error("GetProductsForDdl error:", error);
        },
      });
  }

  private updateHeaderFiltersBadge(): void {
    const countEl = document.getElementById("filtersCount");
    const btn = document.getElementById("filtersTriggerBtn");
    if (!countEl || !btn) return;
    const active = this.appliedProductFilter ? 1 : 0;
    countEl.textContent = String(active);
    btn.classList.toggle("has-active", active > 0);
  }

  /** filterProductOptions narrowed by productSearchTerm - same search-as-you-
   *  type behavior as dashboard-new.component.ts's filterProducts(), just as
   *  a template-driven getter instead of that component's raw-DOM sync. */
  get visibleProductOptions(): ProductFilterOption[] {
    const query = this.productSearchTerm.trim().toLowerCase();
    if (!query) return this.filterProductOptions;
    return this.filterProductOptions.filter((p) =>
      p.name.toLowerCase().includes(query),
    );
  }

  openFilterDrawer(): void {
    this.pendingProductFilter = this.appliedProductFilter;
    this.productSearchTerm = "";
    this.isFilterDrawerOpen = true;
  }

  closeFilterDrawer(): void {
    this.isFilterDrawerOpen = false;
  }

  applyDrawerFilters(): void {
    this.appliedProductFilter = this.pendingProductFilter;
    this.productId = this.appliedProductFilter
      ? this.appliedProductFilter.id || 0
      : 0;
    // Pushed into the shared service so the dashboard (and every other
    // report) picks up this same product on its own next load/init.
    this.scanFilterState.productId = this.productId || null;
    this.scanFilterState.product = this.appliedProductFilter
      ? this.appliedProductFilter.name
      : null;
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
    this.currentPage = 1;
    this.loadDashboard();
  }

  clearDrawerFilters(): void {
    this.pendingProductFilter = null;
    this.appliedProductFilter = null;
    this.productId = 0;
    this.scanFilterState.productId = null;
    this.scanFilterState.product = null;
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
    this.currentPage = 1;
    this.loadDashboard();
  }
  private buildRequest(): QrLifeCycleRequest {
    return {
      compId: this.compId,
      fromDate: this.fromDate,
      toDate: this.toDate,
      productId: this.productId,
      status: this.config.apiStatus,
      search: this.searchTerm.trim() || null,
      pageNo: this.currentPage,
      pageSize: this.rows,
    };
  }
  loadDashboard(): void {
    this.isLoading = true;
    this.loadError = "";
    this.loadErrorState = null;

    this.qrLifeCycleService.getDashboard(this.buildRequest()).subscribe({
      next: (res: QrLifeCycleResponse) => this.applyResponse(res),
      error: (err) => {
        this.isLoading = false;
        this.loadError = apiErrorMessage(err);
        this.loadErrorState = reportErrorState(err);
        console.error("QR lifecycle dashboard load failed", err);
      },
    });
  }

  /** True whenever the latest load attempt failed - shows the full-page
   *  ReportErrorStateComponent in place of the report content, even if a
   *  previous page of data is still sitting in memory. */
  get showFullPageError(): boolean {
    return !!this.loadErrorState;
  }

  private applyResponse(res: QrLifeCycleResponse): void {
    this.insights = res.insights || emptyInsights();
    // No `?.` here - this project's TypeScript (3.4) predates optional
    // chaining, so a plain `|| {}` fallback does the same job.
    const trendGraph =
      res.trendGraph || ({} as Partial<QrLifeCycleResponse["trendGraph"]>);
    this.generatedTrendData = parseTrendGraph(trendGraph.generatedTrendGraph);
    this.assignedTrendData = parseTrendGraph(trendGraph.assignedTrendGraph);
    this.scrappedTrendData = parseTrendGraph(trendGraph.scrappedTrendGraph);
    this.deallocatedTrendData = parseTrendGraph(
      trendGraph.deallocatedTrendGraph,
    );
    this.availableTrendData = parseTrendGraph(trendGraph.availableTrendGraph);
    this.reportData = res.data || [];
    this.totalRecords = res.totalRecords;
    this.totalPages = Math.max(1, res.totalPages);
    this.isLoading = false;
  }
  /** Debounced so `search` (now sent server-side, see buildRequest()) isn't
   *  fired on every keystroke - resets to page 1 once it does fire, same as
   *  any other filter change. */
  onSearch(term: string): void {
    this.searchTerm = term;
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.currentPage = 1;
      this.loadDashboard();
    }, 350);
  }
  refreshData(): void {
    const syncBtn = document.getElementById("syncBtn");
    if (syncBtn) {
      syncBtn.classList.add("is-syncing");
      setTimeout(() => syncBtn.classList.remove("is-syncing"), 700);
    }
    this.loadDashboard();
  }
  onRowsChange(rows: number): void {
    this.rows = rows;
    this.currentPage = 1;
    this.loadDashboard();
  }
  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.rows + 1;
  }
  get rangeEnd(): number {
    return Math.min(this.currentPage * this.rows, this.totalRecords);
  }
  get pageNumbers(): (number | "...")[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: (number | "...")[] = [1];
    if (current - 1 > 2) pages.push("...");
    for (
      let p = Math.max(2, current - 1);
      p <= Math.min(total - 1, current + 1);
      p++
    ) {
      pages.push(p);
    }
    if (current + 1 < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  }
  goToPage(page: number | "..."): void {
    if (page === "..." || page === this.currentPage) return;
    this.currentPage = page;
    this.loadDashboard();
  }
  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadDashboard();
    }
  }
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadDashboard();
    }
  }
  /** "generated" | "assigned" | "deallocated" | "scrapped" -> pill CSS class.
   *  Matches the KPI card accent colors so a status reads consistently
   *  wherever it shows up on the page. */
  statusClass(status: string): string {
    return "gqr-status-pill gqr-status-" + (status || "").toLowerCase();
  }
  isActiveKpi(kpi: QrReportConfig["activeKpi"]): boolean {
    return this.config.activeKpi === kpi;
  }

  /** The decorative curves each spark SVG shipped with before real trend
   *  data existed (analytics.component.ts's TREND_FALLBACK_LINE does the
   *  same thing for its one series) - kept as the fallback for fewer than 2
   *  data points, so a KPI's card backdrop doesn't just go blank. */
  private static readonly TREND_FALLBACKS = {
    generated:
      "M0,70 C30,64 45,72 70,66 C95,60 110,68 140,58 C170,48 185,58 210,44 C235,30 250,38 300,12",
    assigned:
      "M0,66 C30,70 45,60 70,64 C95,68 110,56 140,60 C170,64 185,50 210,52 C235,54 250,42 300,20",
    scrapped:
      "M0,50 C30,46 45,56 70,52 C95,48 110,58 140,54 C170,50 185,60 210,58 C235,56 250,64 300,68",
    deallocated:
      "M0,60 C30,58 45,64 70,60 C95,56 110,62 140,58 C170,54 185,60 210,56 C235,52 250,58 300,50",
    available:
      "M0,68 C30,66 45,70 70,64 C95,58 110,64 140,56 C170,48 185,54 210,46 C235,38 250,44 300,26",
  };

  /** Builds a spark SVG's line + area path data from a parsed trendGraph
   *  series, scaled into its 0 0 300 90 viewBox - same algorithm as
   *  analytics.component.ts's buildTrendPaths(), generalized to take
   *  whichever series/fallback the caller needs instead of just one. */
  private buildTrendPaths(
    data: number[],
    fallbackLine: string,
  ): { line: string; area: string } {
    if (data.length < 2) {
      return { line: fallbackLine, area: `${fallbackLine} L300,90 L0,90 Z` };
    }

    const width = 300;
    const height = 90;
    const padTop = 10;
    // Bigger than analytics.component.ts's equivalent (10): this component's
    // .gqr-kpi-spark box bleeds past the card's own bottom edge (position:
    // absolute; bottom: -10px; height: 50px - see
    // generated-qr-report.component.css), so the card's overflow:hidden
    // clips off the bottom 10 of those 50px (20% of the box). In this 0-90
    // viewBox that clip line sits at y=72 - a flat/near-flat series (e.g. an
    // all-zero trend) computed with only 10px of bottom padding lands around
    // y=73-74, past that line, so it renders but is invisible. 20px keeps
    // even the lowest point (including an all-zero series) comfortably
    // above y=72.
    const padBottom = 20;
    const usableHeight = height - padTop - padBottom;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    // See analytics.component.ts's identical comment: without this, a flat
    // run at the series minimum (e.g. an all-zero trend, or zeros before a
    // single spike) maps to y = height exactly and hugs the card's bottom
    // edge closely enough to read as missing rather than flat.
    const baselinePad = range * 0.1;
    const paddedRange = range + baselinePad;
    const stepX = width / (data.length - 1);

    const points = data.map((v, i) => ({
      x: i * stepX,
      y:
        padTop +
        usableHeight -
        ((v - min + baselinePad) / paddedRange) * usableHeight,
    }));

    const line = points
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`,
      )
      .join(" ");
    const last = points[points.length - 1];
    const first = points[0];
    const area = `${line} L${last.x.toFixed(1)},${height} L${first.x.toFixed(1)},${height} Z`;

    return { line, area };
  }

  get generatedTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.generatedTrendData,
      GeneratedQrReportComponent.TREND_FALLBACKS.generated,
    );
  }
  get assignedTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.assignedTrendData,
      GeneratedQrReportComponent.TREND_FALLBACKS.assigned,
    );
  }
  get scrappedTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.scrappedTrendData,
      GeneratedQrReportComponent.TREND_FALLBACKS.scrapped,
    );
  }
  get deallocatedTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.deallocatedTrendData,
      GeneratedQrReportComponent.TREND_FALLBACKS.deallocated,
    );
  }
  get availableTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.availableTrendData,
      GeneratedQrReportComponent.TREND_FALLBACKS.available,
    );
  }
  // Indian numbering (lakh/crore), not the M/K short-scale — 1L = 1,00,000,
  // 1Cr = 1,00,00,000.
  compact(n: number): string {
    if (n >= 1_00_00_000)
      return (n / 1_00_00_000).toFixed(2).replace(/\.00$/, "") + "Cr";
    if (n >= 1_00_000)
      return (n / 1_00_000).toFixed(2).replace(/\.00$/, "") + "L";
    if (n >= 1_000) return (n / 1_000).toFixed(2).replace(/\.00$/, "") + "K";
    return String(n);
  }
  changeClass(pct: number): string {
    if (pct > 0) return "gqr-up";
    if (pct < 0) return "gqr-bad";
    return "gqr-warn";
  }
  changeSign(pct: number): string {
    return pct > 0 ? "+" : "";
  }

  /** "Aug 26, 2026" - date only, for Mfg/Exp Date. Falls back to the raw
   *  string if it isn't a parseable date - same pattern as
   *  analytics.component.ts's formatDateOnly(). */
  formatDateOnly(raw: string | null | undefined): string {
    if (!raw) return "-";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  /** "Aug 26, 2026 11:19 AM" - for Created On, which carries a real
   *  timestamp off the SP rather than just a date - same pattern as
   *  analytics.component.ts's formatScanTimestamp(). */
  formatTimestamp(raw: string | null | undefined): string {
    if (!raw) return "-";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    const datePart = this.formatDateOnly(raw);
    const timePart = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  }

  /** The on-screen table only ever holds one server-paginated page - export
   *  re-fetches the same filtered request (product/date/status) with
   *  pageSize: -1 so the CSV covers every matching record, not just
   *  whatever page happens to be loaded. Same -1-means-all convention
   *  ProductMasterService.loadProductsForDdl already uses for its own
   *  "give me the whole list" calls. */
  exportTable(): void {
    const request: QrLifeCycleRequest = {
      ...this.buildRequest(),
      pageNo: 1,
      pageSize: -1,
    };
    this.qrLifeCycleService.getDashboard(request).subscribe({
      next: (res: QrLifeCycleResponse) => this.downloadCsv(res.data || []),
      error: (err) => {
        console.error("Export failed to load full report data", err);
        this.downloadCsv(this.reportData);
      },
    });
  }

  private downloadCsv(data: QrLifeCycleReportItem[]): void {
    const headers = [
      "From No",
      "To No",
      "Qty",
      "Product",
      "SKU",
      "Batch No",
      "Mfg Date",
      "Exp Date",
      "Created On",
      "Status",
    ];
    const rows = data.map((r) => [
      String(r.fromNo),
      String(r.toNo),
      String(r.qty),
      r.productNm || "",
      r.productCd || "",
      r.batchNo || "",
      r.mfgDt || "",
      r.expDt || "",
      r.createdDt || "",
      r.status,
    ]);
    const escapeCsv = (val: string) => `"${(val || "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows]
      .map((cols) => cols.map(escapeCsv).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${this.reportType}-qr-report.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
